// Code/src/Service/CredentialingService.ts
// Credentialing Service - Handle credentialing system integrations
import { getListItems, updateListItems } from "./ProfileService";
import { getWithAuth, postWithAuth } from "./InitializeService";
import { getDevImpersonationLoginName } from "../Utility/devImpersonation";
import { formatUserNameForAPI } from "../Utility/Utility";

/**
 * Response from credentialing API
 */
export interface CredentialingRemoveResponse {
  updatedCount: number;
  updatedCredIds: string[];
}

/**
 * Remove own missing credentialing item (self-service)
 * Uses authenticated user's context from JWT token
 *
 * @param missingItem - Document type title (e.g., "Passport", "CPR", "TB Test")
 * @param state - State code (2-letter, e.g., "FL", "CO")
 * @returns Promise with updatedCount and updatedCredIds array
 *
 * @example
 * ```typescript
 * const result = await removeOwnMissingItem("Passport", "FL");
 * console.log(`Updated ${result.updatedCount} credentialing records`);
 * ```
 */
export const removeOwnMissingItem = async (
  missingItem: string,
  state: string,
): Promise<CredentialingRemoveResponse> => {
  // While impersonating (dev only), removeown would hit the signed-in account,
  // so use the admin endpoint for the impersonated user (needs CredentialingAdmin/HR/Admin).
  const impersonateLoginName = getDevImpersonationLoginName();
  const response = impersonateLoginName
    ? await postWithAuth("credentialing/automissingitems/remove", {
        userAccount: formatUserNameForAPI(impersonateLoginName),
        missingItem,
        state,
      })
    : await postWithAuth("credentialing/automissingitems/removeown", {
        missingItem,
        state,
      });
  return response.data;
};

/**
 * Get credentialing items for a user
 * @param fullName - User's full name
 */
export const getCredentialingItems = (fullName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com/credentialing",
    listName: "Credentialing",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='Credentialing_x0020_Person' LookupId='FALSE' /><Value Type='Text'>${fullName}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    rowLimit: 500,
  });
};

/**
 * Update a single credentialing item for the current user
 * @param itemId - SharePoint list item ID of the credentialing record
 * @param fieldValues - Fields to update on the credentialing item
 */
export const updateCredentialingItem = (
  itemId: number,
  fieldValues: Record<string, any>,
) => {
  return updateListItems({
    siteUrl: "https://sharepoint.teampbs.com/credentialing",
    listName: "Credentialing",
    filter: { ID: itemId },
    fieldValues,
    updateMultipleLimit: 1,
  });
};

/**
 * Get counties for a state, following @odata.nextLink until all pages are fetched
 * @param state - State code (2-letter, e.g., "FL", "CO")
 */
export const getCounty = async (state: string) => {
  let nextLink: string | undefined =
    `regionserviced?$select=County&$filter=state eq '${state}'`;
  let response;
  const counties: any[] = [];

  // Safety cap guards against runaway pagination if nextLink is ever malformed
  // (mirrors DocumentStore.fetchDocuments' MAX_PAGES guard - see Task 9813)
  const MAX_PAGES = 50;
  let pageCount = 0;

  try {
    while (nextLink && pageCount < MAX_PAGES) {
      response = await getWithAuth(nextLink);
      const data = response.data;
      counties.push(...(Array.isArray(data) ? data : (data?.value ?? [])));
      nextLink = data?.["@odata.nextLink"];
      pageCount++;
    }
  } catch (error) {
    // A later page failing must not discard counties already fetched
    console.error("Failed to fetch a subsequent page of counties:", error);
  }

  return { ...response, data: { ...response?.data, value: counties } };
};

export const getRBTC = (fullName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "RBT Candidate Tracking",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='Name' LookupId='FALSE' /><Value Type='Text'>${fullName}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    rowLimit: 1,
  });
};

export const getRBT = (fullName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "Registered Behavior Technicians",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='Name' LookupId='FALSE' /><Value Type='Text'>${fullName}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    rowLimit: 1,
  });
};

export const getPBAP = (fullName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "Participants of Behavior Analysis Program",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='Person' LookupId='FALSE' /><Value Type='Text'>${fullName}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    rowLimit: 1,
  });
};

/**
 * Get AWSR (Analysts with Supervision Requirements) information for a user by full name
 * @param fullName - User's full name
 */
export const getAWSR = (fullName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "Analysts with Supervision Requirements",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='Name' /><Value Type='Text'>${fullName}</Value></Eq><Gt><FieldRef Name='ID' /><Value Type='Integer'>0</Value></Gt></And></Where><OrderBy><FieldRef Name='ID' Ascending='False' /></OrderBy></Query></View>`,
    rowLimit: 1,
  });
};
